import { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import type { TacticsDrawing } from '@4cm/core/tactics/drawings';
import { measureDistanceMetres } from '@4cm/core/tactics/drawings';
import { useRegion } from '../../lib/i18n/useRegion';
import { hexWithAlpha, formatMeasureLabel } from './drawingHelpers';

/**
 * 3D-native renderer for coach-authored drawings on the tactics
 * board. Replaces the SVG `DrawingLayer2D` on the 3D surface so the
 * coach's lines / arrows / zones / text annotations project onto the
 * pitch plane with real perspective under every camera preset.
 *
 * Coordinate convention: drawing points are world `{x, z}` metres
 * (y=0 implicit). Each shape's THREE geometry is positioned at
 * Y = DRAWING_Y_M so it floats just above all the analytical
 * overlays (heatmap @ 0.006, voronoi @ 0.005–0.007).
 *
 * Stroke widths are in metres and translate 1:1 because the drei
 * `Line` component's `lineWidth` is interpreted in world units (it
 * uses Line2 from three-stdlib internally).
 *
 * Reuses the shared `hexWithAlpha` + `formatMeasureLabel` helpers
 * from `drawingHelpers.ts` so 2D + 3D layers stay visually consistent.
 */

const DRAWING_Y_M = 0.012;

/** Convert a world `{x, z}` point to a Vector3 at the pitch-plane Y. */
function pt3(p: { x: number; z: number }): [number, number, number] {
  return [p.x, DRAWING_Y_M, p.z];
}

/** Sample a quadratic bezier through (a, ctrl, b) into N+1 evenly
 *  spaced points. */
function sampleQuadratic(
  a: { x: number; z: number },
  ctrl: { x: number; z: number },
  b: { x: number; z: number },
  segments = 15,
): Array<[number, number, number]> {
  const pts: Array<[number, number, number]> = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const inv = 1 - t;
    const x = inv * inv * a.x + 2 * inv * t * ctrl.x + t * t * b.x;
    const z = inv * inv * a.z + 2 * inv * t * ctrl.z + t * t * b.z;
    pts.push([x, DRAWING_Y_M, z]);
  }
  return pts;
}

/** Infer the bezier control point for a 2-point curved arrow — same
 *  perpendicular-midpoint offset as `DrawingLayer2D`. */
function inferCurveControl(
  a: { x: number; z: number },
  b: { x: number; z: number },
): { x: number; z: number } {
  const cx = (a.x + b.x) / 2;
  const cz = (a.z + b.z) / 2;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len === 0) return { x: cx, z: cz };
  const k = 0.15 * len;
  return {
    x: cx - (dz / len) * k,
    z: cz + (dx / len) * k,
  };
}

// ---------------------------------------------------------------------------
// Arrowhead — small cone aligned to a tip + direction
// ---------------------------------------------------------------------------

interface ArrowheadProps {
  tip: { x: number; z: number };
  /** Direction the arrow points (tail → tip). Unit vector preferred but
   *  any non-zero vector works. */
  direction: { x: number; z: number };
  color: string;
  /** Stroke width of the parent shaft — the cone is sized off this. */
  strokeWidth: number;
}

/** Filled triangular arrowhead at `tip`, oriented along `direction`.
 *  Sized as a function of the shaft stroke so the head reads in
 *  proportion to the line it terminates. */
function Arrowhead({ tip, direction, color, strokeWidth }: ArrowheadProps) {
  const quaternion = useMemo(() => {
    // `coneGeometry` default-orients along +Y. We want the cone's tip
    // to point along the 2D (x, z) direction. Build a quaternion that
    // maps (+Y) to that direction.
    const len = Math.sqrt(
      direction.x * direction.x + direction.z * direction.z,
    );
    if (len === 0) return new THREE.Quaternion();
    const dir = new THREE.Vector3(direction.x / len, 0, direction.z / len);
    return new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      dir,
    );
  }, [direction.x, direction.z]);

  // Cone height + radius scale with the shaft so the head feels in
  // proportion across the stroke-width slider's range.
  const headLen = Math.max(0.6, strokeWidth * 4);
  const headRad = Math.max(0.25, strokeWidth * 2);

  // The cone's mesh origin is at its base centre; nudge it back along
  // the direction by headLen/2 so the cone TIP lands at `tip`.
  const baseOffset = useMemo(() => {
    const len = Math.sqrt(
      direction.x * direction.x + direction.z * direction.z,
    );
    if (len === 0) return [tip.x, DRAWING_Y_M, tip.z] as const;
    const bx = tip.x - (direction.x / len) * (headLen / 2);
    const bz = tip.z - (direction.z / len) * (headLen / 2);
    return [bx, DRAWING_Y_M, bz] as const;
  }, [tip.x, tip.z, direction.x, direction.z, headLen]);

  return (
    <mesh
      position={baseOffset as unknown as [number, number, number]}
      quaternion={quaternion}
      raycast={() => undefined}
    >
      <coneGeometry args={[headRad, headLen, 12]} />
      <meshBasicMaterial color={color} transparent depthWrite={false} />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// Per-kind renderers
// ---------------------------------------------------------------------------

interface ShapeProps {
  drawing: TacticsDrawing;
  distanceUnit: 'metres' | 'yards';
}

function LineShape({ drawing }: ShapeProps) {
  if (drawing.points.length < 2) return null;
  const a = drawing.points[0]!;
  const b = drawing.points[drawing.points.length - 1]!;
  return (
    <Line
      points={[pt3(a), pt3(b)]}
      color={drawing.stroke}
      lineWidth={drawing.strokeWidth * 12}
      dashed={drawing.dash !== 'solid' && drawing.dash !== undefined}
      dashSize={drawing.strokeWidth * 4}
      gapSize={drawing.strokeWidth * 2.5}
      transparent
      depthWrite={false}
    />
  );
}

function ArrowShape({ drawing }: ShapeProps) {
  if (drawing.points.length < 2) return null;
  const a = drawing.points[0]!;
  const b = drawing.points[drawing.points.length - 1]!;
  const showHead =
    drawing.arrowHead !== undefined && drawing.arrowHead !== 'none';
  return (
    <group raycast={() => undefined}>
      <Line
        points={[pt3(a), pt3(b)]}
        color={drawing.stroke}
        lineWidth={drawing.strokeWidth * 12}
        dashed={drawing.dash !== 'solid' && drawing.dash !== undefined}
        dashSize={drawing.strokeWidth * 4}
        gapSize={drawing.strokeWidth * 2.5}
        transparent
        depthWrite={false}
      />
      {showHead && (
        <Arrowhead
          tip={b}
          direction={{ x: b.x - a.x, z: b.z - a.z }}
          color={drawing.stroke}
          strokeWidth={drawing.strokeWidth}
        />
      )}
    </group>
  );
}

function CurvedArrowShape({ drawing }: ShapeProps) {
  if (drawing.points.length < 2) return null;
  const a = drawing.points[0]!;
  const b = drawing.points[drawing.points.length - 1]!;
  const ctrl =
    drawing.points.length >= 3 ? drawing.points[1]! : inferCurveControl(a, b);
  const samples = sampleQuadratic(a, ctrl, b, 15);
  // Direction at the end of the curve — derivative of the bezier at
  // t=1 is 2*(b - ctrl).
  const endDir = { x: b.x - ctrl.x, z: b.z - ctrl.z };
  const showHead =
    drawing.arrowHead !== undefined && drawing.arrowHead !== 'none';
  return (
    <group raycast={() => undefined}>
      <Line
        points={samples}
        color={drawing.stroke}
        lineWidth={drawing.strokeWidth * 12}
        dashed={drawing.dash !== 'solid' && drawing.dash !== undefined}
        dashSize={drawing.strokeWidth * 4}
        gapSize={drawing.strokeWidth * 2.5}
        transparent
        depthWrite={false}
      />
      {showHead && (
        <Arrowhead
          tip={b}
          direction={endDir}
          color={drawing.stroke}
          strokeWidth={drawing.strokeWidth}
        />
      )}
    </group>
  );
}

function RectShape({ drawing }: ShapeProps) {
  if (drawing.points.length < 2) return null;
  const a = drawing.points[0]!;
  const b = drawing.points[1]!;
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x);
  const minZ = Math.min(a.z, b.z);
  const maxZ = Math.max(a.z, b.z);
  const corners: Array<[number, number, number]> = [
    [minX, DRAWING_Y_M, minZ],
    [maxX, DRAWING_Y_M, minZ],
    [maxX, DRAWING_Y_M, maxZ],
    [minX, DRAWING_Y_M, maxZ],
    [minX, DRAWING_Y_M, minZ],
  ];
  const hasFill = drawing.fill && drawing.fill !== 'transparent';
  const fill = hasFill ? drawing.fill! : null;
  // Build a flat fill geometry only when needed — a rect is cheap, but
  // the fill mesh allocation is wasted for outline-only shapes.
  const fillGeom = useMemo(() => {
    if (!fill) return null;
    const shape = new THREE.Shape();
    shape.moveTo(minX, -maxZ);
    shape.lineTo(maxX, -maxZ);
    shape.lineTo(maxX, -minZ);
    shape.lineTo(minX, -minZ);
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, [fill, minX, maxX, minZ, maxZ]);
  return (
    <group raycast={() => undefined}>
      {fillGeom && (
        <mesh
          geometry={fillGeom}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, DRAWING_Y_M - 0.0005, 0]}
        >
          <meshBasicMaterial
            color={fill ?? '#ffffff'}
            transparent
            opacity={0.45}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      <Line
        points={corners}
        color={drawing.stroke}
        lineWidth={drawing.strokeWidth * 12}
        dashed={drawing.dash !== 'solid' && drawing.dash !== undefined}
        dashSize={drawing.strokeWidth * 4}
        gapSize={drawing.strokeWidth * 2.5}
        transparent
        depthWrite={false}
      />
    </group>
  );
}

function EllipseShape({ drawing }: ShapeProps) {
  if (drawing.points.length < 2) return null;
  const a = drawing.points[0]!;
  const b = drawing.points[1]!;
  const cx = (a.x + b.x) / 2;
  const cz = (a.z + b.z) / 2;
  const rx = Math.abs(b.x - a.x) / 2;
  const rz = Math.abs(b.z - a.z) / 2;
  // Sample 48 points around the ellipse for a smooth perimeter.
  const segments = 48;
  const points: Array<[number, number, number]> = [];
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * Math.PI * 2;
    points.push([cx + Math.cos(t) * rx, DRAWING_Y_M, cz + Math.sin(t) * rz]);
  }
  const hasFill = drawing.fill && drawing.fill !== 'transparent';
  return (
    <group raycast={() => undefined}>
      {hasFill && (
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[cx, DRAWING_Y_M - 0.0005, cz]}
        >
          <circleGeometry args={[1, segments]} />
          <meshBasicMaterial
            color={drawing.fill!}
            transparent
            opacity={0.45}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      <Line
        points={points}
        color={drawing.stroke}
        lineWidth={drawing.strokeWidth * 12}
        dashed={drawing.dash !== 'solid' && drawing.dash !== undefined}
        dashSize={drawing.strokeWidth * 4}
        gapSize={drawing.strokeWidth * 2.5}
        transparent
        depthWrite={false}
      />
    </group>
  );
}

function FreehandShape({ drawing }: ShapeProps) {
  if (drawing.points.length < 2) return null;
  const points = drawing.points.map(pt3);
  return (
    <Line
      points={points}
      color={drawing.stroke}
      lineWidth={drawing.strokeWidth * 12}
      dashed={drawing.dash !== 'solid' && drawing.dash !== undefined}
      dashSize={drawing.strokeWidth * 4}
      gapSize={drawing.strokeWidth * 2.5}
      transparent
      depthWrite={false}
    />
  );
}

function TextShape({ drawing }: ShapeProps) {
  if (drawing.points.length < 1) return null;
  const anchor = drawing.points[0]!;
  return (
    <Text
      position={[anchor.x, DRAWING_Y_M + 0.001, anchor.z]}
      rotation={[-Math.PI / 2, 0, 0]}
      color={drawing.stroke}
      fontSize={2.4}
      anchorX="center"
      anchorY="middle"
      raycast={() => undefined}
    >
      {drawing.text ?? ''}
    </Text>
  );
}

function MeasureShape({ drawing, distanceUnit }: ShapeProps) {
  if (drawing.points.length < 2) return null;
  const a = drawing.points[0]!;
  const b = drawing.points[drawing.points.length - 1]!;
  const distM = measureDistanceMetres(a, b);
  const label = formatMeasureLabel(distM, distanceUnit);
  // Midpoint offset perpendicular to the segment so the label clears
  // the line — mirrors the 2D layer's 1.6 m offset.
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  const OFFSET = 1.6;
  const nx = len === 0 ? 0 : -dz / len;
  const nz = len === 0 ? -1 : dx / len;
  const mx = (a.x + b.x) / 2 + nx * OFFSET;
  const mz = (a.z + b.z) / 2 + nz * OFFSET;
  return (
    <group raycast={() => undefined}>
      <Line
        points={[pt3(a), pt3(b)]}
        color={drawing.stroke}
        lineWidth={drawing.strokeWidth * 12}
        dashed
        dashSize={drawing.strokeWidth * 4}
        gapSize={drawing.strokeWidth * 2.5}
        transparent
        depthWrite={false}
      />
      <Text
        position={[mx, DRAWING_Y_M + 0.001, mz]}
        rotation={[-Math.PI / 2, 0, 0]}
        color={drawing.stroke}
        fontSize={1.8}
        anchorX="center"
        anchorY="middle"
        raycast={() => undefined}
      >
        {label}
      </Text>
    </group>
  );
}

function ZoneShape({ drawing }: ShapeProps) {
  if (drawing.points.length < 3) return null;
  const points = drawing.points;
  const fill =
    drawing.fill && drawing.fill !== 'auto' && drawing.fill !== 'transparent'
      ? drawing.fill
      : hexWithAlpha(drawing.stroke, 0.22);
  // Build the polygon's ShapeGeometry — same pattern as VoronoiLayer3D.
  const geometry = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(points[0]!.x, -points[0]!.z);
    for (let i = 1; i < points.length; i++) {
      shape.lineTo(points[i]!.x, -points[i]!.z);
    }
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, [points]);
  // Closed outline.
  const outline: Array<[number, number, number]> = [
    ...points.map(pt3),
    pt3(points[0]!),
  ];
  return (
    <group raycast={() => undefined}>
      <mesh
        geometry={geometry}
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, DRAWING_Y_M - 0.0005, 0]}
      >
        <meshBasicMaterial
          color={fill}
          transparent
          opacity={1}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <Line
        points={outline}
        color={drawing.stroke}
        lineWidth={drawing.strokeWidth * 12}
        dashed={drawing.dash !== 'solid' && drawing.dash !== undefined}
        dashSize={drawing.strokeWidth * 4}
        gapSize={drawing.strokeWidth * 2.5}
        transparent
        depthWrite={false}
      />
    </group>
  );
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

function DrawingShape3D(props: ShapeProps) {
  switch (props.drawing.kind) {
    case 'line':
      return <LineShape {...props} />;
    case 'arrow':
      return <ArrowShape {...props} />;
    case 'curved-arrow':
      return <CurvedArrowShape {...props} />;
    case 'rect':
      return <RectShape {...props} />;
    case 'ellipse':
      return <EllipseShape {...props} />;
    case 'text':
      return <TextShape {...props} />;
    case 'freehand':
      return <FreehandShape {...props} />;
    case 'measure':
      return <MeasureShape {...props} />;
    case 'zone':
      return <ZoneShape {...props} />;
    default:
      return null;
  }
}

export interface DrawingLayer3DProps {
  drawings: ReadonlyArray<TacticsDrawing>;
}

function DrawingLayer3DInner({ drawings }: DrawingLayer3DProps) {
  // Region drives the measure-tool label unit.
  const region = useRegion();
  const distanceUnit = region.units.distance;
  if (!drawings || drawings.length === 0) return null;
  return (
    <group raycast={() => undefined} renderOrder={4}>
      {drawings.map((d) => (
        <DrawingShape3D key={d.id} drawing={d} distanceUnit={distanceUnit} />
      ))}
    </group>
  );
}

export const DrawingLayer3D = memo(DrawingLayer3DInner);
